import { InfoCircleOutlined } from '../../components/icons'
import { Chip } from '../../components/ui/Chip'
import type { components } from '../../lib/api/schema.gen'
import { CustomLeadTimeControl } from './CustomLeadTimeControl'
import type { RegionTierId } from './custom-region-tiers'
import { deriveRegionTierOptions } from './custom-region-tiers'
import { deriveShipmentGroups } from './ship-as-ready-breakdown'
import type { ReadinessBucket, ShipmentGroup } from './ship-as-ready-breakdown'

type LineItem = components['schemas']['LineItem']
type CartLinePresentation = components['schemas']['CartLinePresentation']

export interface ShipmentBreakdownProps {
  readonly lines: readonly LineItem[]
  readonly presentationsBySourceableId: ReadonlyMap<string, CartLinePresentation>
  /** `null` (code-review M1) when the persisted tier id isn't one this FE
   * table recognizes — see `CustomLeadTimeControl`'s doc for why this must
   * NOT be coerced into a default tier. */
  readonly customLeadTimeTierId: RegionTierId | null
  readonly onSelectCustomLeadTimeTier: (tierId: RegionTierId) => void
  /** Disables the custom card's lead-time control while a tier-change round
   * trip (UID-271, `PUT /cart/lines/{sourceableId}/lead-time-tier`) is
   * pending. */
  readonly customLeadTimeTierBusy?: boolean
  /** Injectable for deterministic tests; defaults to "now" for real use. */
  readonly today?: Date
}

/** Non-custom bucket's "in stock / where it ships from" note — the
 * prototype's `_splitDefs` fixed-card notes (`Fictiv Platform.dc.html`
 * line ~9018): "In stock, local" for STANDARD, the region label for
 * CONFIGURABLE (North America). The CUSTOM card doesn't use this table — its
 * own note names the currently selected sourcing region instead. */
const FIXED_BUCKET_NOTE: Readonly<Partial<Record<ReadinessBucket, string>>> = {
  standard: 'In stock, local',
  configurable: 'North America',
}

/**
 * UID-96 (C1) / UID-271 (Part B) — the "Ship as ready" multi-shipment
 * breakdown: one card per readiness bucket present (STANDARD / CONFIGURABLE /
 * CUSTOM), each labelled "SHIPMENT N" with its own arrival date and
 * part count. Realigned to the prototype's split-card layout
 * (`shipCard.isSplit`, `Fictiv Platform.dc.html` lines ~2103-2155): small
 * FIXED cards (non-custom buckets) stack on the LEFT; the CUSTOM bucket gets
 * its own HIGHLIGHTED card on the RIGHT, carrying the FABRICATED badge and
 * the sourcing-region + lead-time control (C2).
 */
export function ShipmentBreakdown({
  lines,
  presentationsBySourceableId,
  customLeadTimeTierId,
  onSelectCustomLeadTimeTier,
  customLeadTimeTierBusy = false,
  today = new Date(),
}: ShipmentBreakdownProps) {
  const groups = deriveShipmentGroups(lines, presentationsBySourceableId)

  if (groups.length === 0) {
    return null
  }

  const fixedGroups = groups.filter((group) => group.bucket !== 'custom')
  const customGroup = groups.find((group) => group.bucket === 'custom')
  // Code-review Fix 3 — derived exactly ONCE here (not again inside
  // `CustomLeadTimeControl`, which used to re-run this same derivation from
  // the same four inputs): this component needs it for the custom card's
  // own sourcing-region note (below), and the control needs it to render
  // its trigger/popdown — so it's derived here and passed down, rather than
  // computed twice per render.
  const customTierOptions = customGroup
    ? deriveRegionTierOptions(
        today,
        customGroup.subtotalAmount,
        customGroup.currency,
        customLeadTimeTierId,
      )
    : []
  // The custom card's own sourcing-region note names whichever region is
  // currently selected (e.g. "Overseas", "United States") — the SAME
  // derived options the lead-time control itself renders, so the two can't
  // disagree.
  const selectedRegionLabel = customTierOptions.find((option) => option.selected)?.region

  return (
    <div className="shipment-breakdown" data-testid="shipment-breakdown">
      <div className="shipment-breakdown-columns">
        {fixedGroups.length > 0 && (
          <div className="shipment-breakdown-fixed-col">
            {fixedGroups.map((group) => (
              <FixedShipmentCard key={group.bucket} group={group} />
            ))}
          </div>
        )}
        {customGroup && (
          <div className="shipment-breakdown-custom-col">
            <div className="shipment-breakdown-card shipment-breakdown-card-custom">
              <div className="shipment-breakdown-card-head">
                <span className="shipment-breakdown-bucket-badge" data-bucket="custom">
                  {customGroup.shipmentLabel}
                </span>
              </div>
              <p className="shipment-breakdown-arrive">{customGroup.arriveLabel}</p>
              <div className="shipment-breakdown-custom-meta-row">
                <span className="shipment-breakdown-meta">
                  {customGroup.lineCount} {customGroup.lineCount === 1 ? 'part' : 'parts'}
                  {selectedRegionLabel ? ` · ${selectedRegionLabel}` : ''}
                </span>
                <Chip
                  tone="brand"
                  className="shipment-breakdown-fabricated-badge"
                  icon={<InfoCircleOutlined />}
                  title="Custom parts are made to order — lead time depends on which region builds them, so you can choose the option that fits your timeline."
                >
                  FABRICATED
                </Chip>
              </div>
              <CustomLeadTimeControl
                options={customTierOptions}
                disabled={customLeadTimeTierBusy}
                onSelect={onSelectCustomLeadTimeTier}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function FixedShipmentCard({ group }: { readonly group: ShipmentGroup }) {
  return (
    <div className="shipment-breakdown-card shipment-breakdown-card-fixed">
      <div className="shipment-breakdown-fixed-main">
        <span className="shipment-breakdown-bucket-badge" data-bucket={group.bucket}>
          {group.shipmentLabel}
        </span>
        <span className="shipment-breakdown-fixed-date">{group.arriveLabel}</span>
      </div>
      <span className="shipment-breakdown-fixed-note">
        {group.lineCount} {group.lineCount === 1 ? 'part' : 'parts'} ·{' '}
        {FIXED_BUCKET_NOTE[group.bucket]}
      </span>
    </div>
  )
}
